import { Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ItemInfrastructureDto } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemEntity } from '@/types/api';

import { useItemTemplateSchema } from '../api/get-item-template-schema';
import { useItemLockToggle } from '../api/item-lock-toggle';

import { ItemFormData } from './forms';
import { ItemForm } from './item-form';

const readItemPermissions = [permission.item.read];
const lockPermissions = [permission.item.lock];
const unlockPermissions = [permission.item.unlock];

export interface ViewItemContentProps {
  item: ItemEntity;
  /** Defer the template fetch until the host surface is actually shown. */
  enabled?: boolean;
}

/**
 * Read-only rendering of an item's template-driven attributes (disabled form).
 * Shared by the view dialog below and the Load List graph side panel
 * (MT1-1417), so both surfaces show identical attribute content.
 */
export const ViewItemContent = ({ item, enabled = true }: ViewItemContentProps) => {
  const { t } = useTranslation();

  const {
    data: itemTemplate,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useItemTemplateSchema(item.itemTemplateId, {
    queryConfig: { enabled: enabled && !!item.itemTemplateId },
  });

  const isLoading = isLoadingTemplate;
  const hasError = templateError;

  const formData = useMemo((): ItemFormData => {
    return {
      id: item.id,
      itemNumber: item.itemNumber,
      name: item.name,
      attributes: item.attributes,
      location: item.location,
      adjacentLocation: item.adjacentLocation,
      constructionTypeId: item.constructionTypeId,
      systemIds: item.systemIds ?? [],
      status: item.status,
      quotationTypeId: item.quotationTypeId,
      locationTypeId: item.locationTypeId,
      infrastructures: (item.infrastructures ?? []) as ItemInfrastructureDto[],
      circuits: item.circuits ?? [],
      infrastructureRelationshipConfigs: item.infrastructureRelationshipConfigs ?? [],
    };
  }, [item]);

  return isLoading ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  ) : hasError ? (
    <Box textAlign="center" py={4}>
      <Typography color="error">{t('items.view.error', 'Failed to load item data')}</Typography>
    </Box>
  ) : itemTemplate ? (
    <OidcProtectedComponent
      requireAll={readItemPermissions}
      context={{
        itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
      }}
      fallbackElement={
        <Typography color="error">
          {t('common.unauthorized', 'You do not have permission to view this item')}
        </Typography>
      }
    >
      <ItemForm
        template={itemTemplate}
        disabled={true}
        onSubmit={() => {
          /* noop for read-only view */
        }}
        initialData={formData}
      />
    </OidcProtectedComponent>
  ) : (
    <Box textAlign="center" py={4}>
      <Typography color="textSecondary">{t('items.view.noData', 'No item data found')}</Typography>
    </Box>
  );
};

export interface ViewItemDialogProps {
  item: ItemEntity;
  open: boolean;
  onClose: () => void;
}

/**
 * Controlled read-only view of an item's attributes. Renders the item's
 * template-driven form in disabled mode plus a lock/unlock action. Kept
 * separate from the icon-button trigger so it can be opened from other
 * surfaces (e.g. clicking a node on the Load List graph — MT1-1417).
 */
export const ViewItemDialog = ({ item, open, onClose }: ViewItemDialogProps) => {
  const { t } = useTranslation();
  const subject = useScopeIds();

  const { mutateAsync: toggleLock, isPending: isTogglingLock } = useItemLockToggle(item.id);

  const isDisabled = isTogglingLock;

  const isLocked = item.locked;

  const handleToggleLock = async () => {
    await toggleLock(!item.locked);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('items.view.title', 'View Item')}</DialogTitle>
      <DialogContent>
        <ViewItemContent item={item} enabled={open} />
      </DialogContent>
      <DialogActions>
        <OidcProtectedComponent
          requireAll={isLocked ? unlockPermissions : lockPermissions}
          subject={{ ...subject }}
          context={{
            itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
          }}
        >
          <Button
            type="button"
            variant="contained"
            color={isLocked ? 'success' : 'error'}
            disabled={isDisabled}
            onClick={() => void handleToggleLock()}
            sx={{ mr: 'auto' }}
          >
            {isLocked ? t('common.unlock') : t('common.lock')}
          </Button>
        </OidcProtectedComponent>
        <Button onClick={onClose} disabled={isDisabled}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface ViewItemButtonProps {
  item: ItemEntity;
}

export const ViewItemButton = ({ item }: ViewItemButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={t('items.view.tooltip', 'View item')}>
        <IconButton size="small" onClick={handleOpen} color="primary">
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ViewItemDialog item={item} open={open} onClose={handleClose} />
    </>
  );
};
